/*
 * @Author: liuhang liuhang@nextmar.com
 * @Date: 2025-08-05 13:40:00
 * @FilePath: /code/zeelool-nextjs/src/store/slices/globalDialogSlice.ts
 * @Description: 全局弹窗状态管理slice
 */
import { StateCreator } from 'zustand';

import { Store } from '@/store';
// CannotSubmitDialog 相关类型定义
export interface Product {
  img_gray?: string;
  img: string;
  msg?: string;
  short_name: string;
  qty: number;
  is_glass?: number; // 是否为眼镜商品，1表示是，0表示否
}

export interface CannotSubmitData {
  show: boolean;
  title?: string;
  /** HTML 字符串，来自后端 */
  content?: string;
  /** 商品列表 */
  products?: Product[];
  code?: number; // 1下架 2售罄 3库存不足 4每人每商品限购 5每单每商品限购 6每单多商品限购 7处方不匹配
  /** 确认按钮文案，默认 OK */
  confirmBtnText?: string;
  /** 关闭回调 */
  onClose: () => void;
}

/** 商品信息 */
export interface ICheckoutProduct {
  img?: string;
  qty?: number | string;
  short_name?: string;
  is_glass?: number; // 是否为眼镜商品，1表示是，0表示否
}

/** 列表分组 */
export interface ICannotCheckoutItem {
  /** HTML 内容，支持换行 */
  content?: string;
  products?: ICheckoutProduct[];
}

export interface CannotCheckoutData {
  title?: string;
  list: ICannotCheckoutItem[];
  show: boolean;
  /** 关闭回调 */
  onClose: () => void;
  /** 确认按钮文案，默认 OK */
  confirmBtnText?: string;
}
// AB测试相关类型定义
export interface ABTestButton {
  button_color?: string;
  button_copy?: string;
  button_hover_color?: string;
  copywriting_color?: string;
  copywriting_copy?: string;
  jump_target?: any; // 跳转目标配置
}

export interface ABTestTitle {
  title_color?: string;
  title_copy?: string;
}

export interface ABTestJoinMember {
  title_color?: string;
  title_text?: string;
}

export interface ABTestInfo {
  button?: ABTestButton;
  title?: ABTestTitle;
  join_member?: ABTestJoinMember;
}

// 快捷领券弹窗相关类型定义
export interface QuickCouponDialog {
  show: boolean;
  couponList: any[];
  loading: boolean;
  eventType: string;
  loginSituation: string;
  triggerType: 'auto' | 'manual'; // 新增：区分自动弹出还是手动点击
  // AB测试相关状态
  loginABInfo: ABTestInfo;
  registerABInfo: ABTestInfo;
  autoCouponInfo: ABTestInfo;
  isChannelCoupon: boolean; // 是否为渠道优惠券
}

// 问卷调查弹窗相关类型定义
export interface SurveyDialog {
  show: boolean;
  surveyInfo: {
    title?: string;
    desc?: string;
    reward_type?: number; // 0无，1：优惠券，2:赠品
    reward_info?: {
      coupon_id?: number;
      coupon_title?: string;
      model_type?: number;
    };
    third_id?: string;
    coupon?: any; // 优惠券规则
    gift?: {
      product?: any; // 商品详情
    };
    showCoupon?: boolean; // 是否显示优惠券
    showGift?: boolean; // 是否显示赠品
    [key: string]: any;
  };
}

// 营销场景优惠券弹窗相关类型定义
export interface MarketingCouponDialog {
  show: boolean;
  marketingCouponInfo: {
    pay_success_send_coupons?: any[]; // 支付成功发放的优惠券列表
    pay_success_send_coupon_title?: string; // 弹窗标题
    [key: string]: any;
  };
}

// 价格变动弹窗相关类型定义
export interface PriceChangeDialog {
  show: boolean;
}

// AWS Captcha 弹窗相关类型定义
export interface AwsCaptchaDialog {
  show: boolean;
}

// 动态资源弹窗相关类型定义（支持通过key动态获取资源内容）
export interface DynamicResourceDialog {
  show: boolean;
  key?: string; // 资源key，用于调用接口获取数据
  title?: string;
  pc_explanation?: string;
  wap_explanation?: string;
  popup_type?: string;
}

export interface SiteRedirectInfo {
  fromTrigger: '更改币种语言' | 'IP匹配' | '';
  targetUrl: string;
  popStatus: number; // 0 - 不弹，1 - 首次，2 - 二次
  popMsg: {
    main_title?: string;
    subtitle?: string;
    from_national_flag?: string;
    to_national_flag?: string;
    jump_country_code?: string;
    button_copy?: string;
    staying_copy?: string;
    not_reminding?: string;
  };
}

// 其他全局弹窗可以在这里添加类型定义
// 例如: VipCouponDialog, JoinFreeDialog 等

export interface GlobalDialogState {
  // 快捷领券弹窗相关状态
  quickCouponDialog: QuickCouponDialog;

  // 问卷调查引导弹窗相关状态
  surveyGuideDialog: SurveyDialog;

  // 赠品弹窗相关状态
  giftDialog: {
    show: boolean;
  };

  // 营销场景优惠券弹窗相关状态
  marketingCouponDialog: MarketingCouponDialog;

  // 价格变动弹窗相关状态
  priceChangeDialog: PriceChangeDialog;

  // AWS Captcha 弹窗相关状态
  awsCaptchaDialog: AwsCaptchaDialog;

  // 动态资源弹窗相关状态
  dynamicResourceDialog: DynamicResourceDialog;

  cannotSubmitData: CannotSubmitData; // 限购弹窗数据
  vipCouponDialogInfo: {
    show: boolean;
    couponList: any[];
    tokenTitle: string;
    jumpUrlData: any;
  }; // 是否显示Vip优惠券弹窗
  cannotCheckoutData: CannotCheckoutData; // 无法结算弹窗数据

  // 购物车点击结算触发登录弹窗，如果直接跳转到payment页面，则不需要弹券弹窗
  needFastRegisterCoupon: boolean;

  // 国家站跳转
  siteRedirectInfo: SiteRedirectInfo;
}

export interface GlobalDialogActions {
  // 快捷领券弹窗相关操作方法
  setQuickCouponDialog: (quickCouponDialog: Partial<QuickCouponDialog>) => void;
  setQuickCouponDialogShow: (show: boolean) => void;
  setQuickCouponDialogLoading: (loading: boolean) => void;
  setQuickCouponDialogEventType: (eventType: string) => void;
  setQuickCouponDialogLoginSituation: (loginSituation: string) => void;
  setQuickCouponDialogTriggerType: (triggerType: 'auto' | 'manual') => void;

  // 问卷调查引导弹窗相关操作方法
  setSurveyGuideDialog: (surveyDialog: Partial<SurveyDialog>) => void;
  setSurveyGuideDialogShow: (show: boolean) => void;
  setSurveyGuideDialogInfo: (surveyInfo: SurveyDialog['surveyInfo']) => void;

  // 营销场景优惠券弹窗相关操作方法
  setMarketingCouponDialog: (marketingCouponDialog: Partial<MarketingCouponDialog>) => void;
  setMarketingCouponDialogShow: (show: boolean) => void;
  setMarketingCouponInfo: (
    marketingCouponInfo: MarketingCouponDialog['marketingCouponInfo'],
  ) => void;

  // 赠品弹窗相关操作方法
  setGiftDialogShow: (show: boolean) => void;

  // 价格变动弹窗相关操作方法
  setPriceChangeDialogShow: (show: boolean) => void;

  // AWS Captcha 弹窗相关操作方法
  setAwsCaptchaDialogShow: (show: boolean) => void;

  // 动态资源弹窗相关操作方法
  setDynamicResourceDialog: (dialog: Partial<DynamicResourceDialog>) => void;
  setDynamicResourceDialogShow: (show: boolean) => void;

  setCannotSubmitData: (cannotSubmitData: GlobalDialogState['cannotSubmitData']) => void;
  setVipCouponDialogInfo: (vipCouponDialogInfo: GlobalDialogState['vipCouponDialogInfo']) => void;
  setCannotCheckoutData: (cannotCheckoutData: GlobalDialogState['cannotCheckoutData']) => void;

  // 购物车点击结算触发登录弹窗，如果直接跳转到payment页面，则不需要弹券弹窗
  setNeedFastRegisterCoupon: (show: boolean) => void;

  setSiteRedirectInfo: (siteRedirectInfo: GlobalDialogState['siteRedirectInfo']) => void;
}

export type GlobalDialogSlice = GlobalDialogState & GlobalDialogActions;

export const initialGlobalDialogState: GlobalDialogState = {
  // 快捷领券弹窗初始状态
  quickCouponDialog: {
    show: false,
    couponList: [],
    loading: false,
    eventType: 'other',
    loginSituation: '其他',
    triggerType: 'auto',
    // AB测试相关状态初始值
    loginABInfo: {},
    registerABInfo: {},
    autoCouponInfo: {},
    isChannelCoupon: false, // 是否为渠道优惠券
  },

  // 问卷调查引导弹窗初始状态
  surveyGuideDialog: {
    show: false,
    surveyInfo: {},
  },

  // 营销场景优惠券弹窗初始状态
  marketingCouponDialog: {
    show: false,
    marketingCouponInfo: {},
  },

  giftDialog: {
    show: false,
  },

  // 价格变动弹窗初始状态
  priceChangeDialog: {
    show: false,
  },

  // AWS Captcha 弹窗初始状态
  awsCaptchaDialog: {
    show: false,
  },

  // 动态资源弹窗初始状态
  dynamicResourceDialog: {
    show: false,
    key: '',
    title: '',
    pc_explanation: '',
    wap_explanation: '',
    popup_type: '',
  },

  // 后续可以添加其他全局弹窗初始状态
  cannotSubmitData: {
    show: false,
    title: '',
    content: '',
    code: 0,
    products: [],
    onClose: () => {},
    confirmBtnText: 'OK',
  },
  vipCouponDialogInfo: {
    show: false,
    couponList: [],
    tokenTitle: '',
    jumpUrlData: {},
  },
  cannotCheckoutData: {
    show: false,
    list: [],
    onClose: () => {},
    confirmBtnText: 'Back to Cart',
  },

  // 购物车点击结算触发登录弹窗，如果直接跳转到payment页面，则不需要弹券弹窗
  needFastRegisterCoupon: true,

  siteRedirectInfo: {
    popStatus: 0,
    targetUrl: '',
    fromTrigger: '',
    popMsg: {},
  },
};

export const createGlobalDialogSlice: StateCreator<Store, [], [], GlobalDialogSlice> = (set) => {
  return {
    // 使用初始状态
    ...initialGlobalDialogState,

    // 快捷领券弹窗相关操作方法
    setQuickCouponDialog: (dialog) => {
      set((state) => {
        // 如果快捷领券弹窗显示，且登录注册弹窗显示，则关闭快捷领券弹窗
        if (dialog.show && state.showLoginRegisterDialog) {
          dialog.show = false;
        }
        return {
          quickCouponDialog: { ...state.quickCouponDialog, ...dialog },
        };
      });
    },

    setQuickCouponDialogShow: (show) => {
      set((state) => {
        // 如果快捷领券弹窗显示，且登录注册弹窗显示，则关闭快捷领券弹窗
        if (show && state.showLoginRegisterDialog) {
          show = false;
        }
        return {
          quickCouponDialog: { ...state.quickCouponDialog, show },
        };
      });
    },

    setQuickCouponDialogLoading: (loading) => {
      set((state) => ({
        quickCouponDialog: { ...state.quickCouponDialog, loading },
      }));
    },

    setQuickCouponDialogEventType: (eventType) => {
      set((state) => ({
        quickCouponDialog: { ...state.quickCouponDialog, eventType },
      }));
    },

    setQuickCouponDialogLoginSituation: (loginSituation) => {
      set((state) => ({
        quickCouponDialog: { ...state.quickCouponDialog, loginSituation },
      }));
    },

    setQuickCouponDialogTriggerType: (triggerType) => {
      set((state) => ({
        quickCouponDialog: { ...state.quickCouponDialog, triggerType },
      }));
    },

    // 问卷调查弹窗相关操作方法
    setSurveyGuideDialog: (surveyDialog) => {
      set((state) => ({
        surveyGuideDialog: { ...state.surveyGuideDialog, ...surveyDialog },
      }));
    },

    setSurveyGuideDialogShow: (show) => {
      set((state) => ({
        surveyGuideDialog: { ...state.surveyGuideDialog, show },
      }));
    },

    setSurveyGuideDialogInfo: (surveyInfo) => {
      set((state) => ({
        surveyGuideDialog: { ...state.surveyGuideDialog, surveyInfo },
      }));
    },

    // 营销场景优惠券弹窗相关操作方法
    setMarketingCouponDialog: (marketingCouponDialog) => {
      set((state) => ({
        marketingCouponDialog: { ...state.marketingCouponDialog, ...marketingCouponDialog },
      }));
    },

    setMarketingCouponDialogShow: (show) => {
      set((state) => ({
        marketingCouponDialog: { ...state.marketingCouponDialog, show },
      }));
    },

    setMarketingCouponInfo: (marketingCouponInfo) => {
      set((state) => ({
        marketingCouponDialog: { ...state.marketingCouponDialog, marketingCouponInfo },
      }));
    },

    // 赠品弹窗相关操作方法
    setGiftDialogShow: (show) => {
      set((state) => ({
        giftDialog: { ...state.giftDialog, show },
      }));
    },

    // 价格变动弹窗相关操作方法
    setPriceChangeDialogShow: (show) => {
      set((state) => ({
        priceChangeDialog: { ...state.priceChangeDialog, show },
      }));
    },

    // AWS Captcha 弹窗相关操作方法
    setAwsCaptchaDialogShow: (show) => {
      set((state) => ({
        awsCaptchaDialog: { ...state.awsCaptchaDialog, show },
      }));
    },

    // 动态资源弹窗相关操作方法
    setDynamicResourceDialog: (dialog) => {
      set((state) => ({
        dynamicResourceDialog: { ...state.dynamicResourceDialog, ...dialog },
      }));
    },

    setDynamicResourceDialogShow: (show) => {
      set((state) => ({
        dynamicResourceDialog: { ...state.dynamicResourceDialog, show },
      }));
    },

    // 后续可以添加其他全局弹窗操作方法
    ...initialGlobalDialogState,
    setVipCouponDialogInfo: (vipCouponDialogInfo) => set({ vipCouponDialogInfo }),

    // CannotSubmitDialog 相关操作方法
    setCannotSubmitData: (cannotSubmitData) => set({ cannotSubmitData }),

    // CannotCheckoutDialog 相关操作方法
    setCannotCheckoutData: (cannotCheckoutData) => set({ cannotCheckoutData }),

    // 购物车点击结算触发登录弹窗，如果直接跳转到payment页面，则不需要弹券弹窗
    setNeedFastRegisterCoupon: (show) => set({ needFastRegisterCoupon: show }),

    setSiteRedirectInfo: (siteRedirectInfo) => set({ siteRedirectInfo }),
  };
};
